import { useBitrixStore } from '@/shared/store/useBitrixStore';
import { useUserStore } from '@/shared/store/useUserStore';
import {
  Ref,
  computed,
  ref,
} from 'vue';

interface EmailChange {
  isRequestOldEmailOnServiceByBitrixIdDone: Ref<boolean>,
  isOldEmailFoundOnServiceByBitrixId : Ref<boolean>,
  isFoundDifferentEmailOnServiceByBitrixId : Ref<boolean>,
  isReplaceEmailError: Ref<boolean>,
  showCompleteEmailChange: Ref<boolean>,
  needReplace: Ref<boolean>,
  needToFindOldEmailOnServiceByBitrixId: Ref<boolean>,
  appLoader: Ref<boolean>,
  hasContactOnService: Ref<boolean | undefined>,
  isNewClient: Ref<boolean>,
  isCurrentClient: Ref<boolean>,
  isOldClient: Ref<boolean>,
  hasFieldChangeEmailByClient: Ref<boolean>,
  hasFieldChangeEmailByManager: Ref<boolean>,
  hasBitrixId: Ref<boolean>,
  fieldChangeEmailByClient: Ref<string | null>,
  fieldChangeEmailByManager: Ref<string | null>,
  isChangeEmailConfirmed: Ref<boolean>,
  isReplaceEmailCompleted: Ref<boolean>,
  // Гетеры для вывода сообщений
  bannerNewByChangeEmailNotConfirmed: Ref<boolean>,
  bannerNewByChangeB24: Ref<boolean>,
  modalEmailChangeCompleted: Ref<boolean>,
  bannerOldClient: Ref<boolean>,
  bannerNeedRegistration: Ref<boolean>,
  bannerEmailsDontMatch: Ref<boolean>,
  modalEmailChangeBlocked: Ref<boolean>,
  bannerBadReplace: Ref<boolean>,
  bannerAfterEmailChange: Ref<boolean>,
  bannerAfterEmailChangeOldEmails: Ref<boolean>,
  bannerAfterEmailChangeDifferentEmail: Ref<boolean>,
  hasEmailBanner: Ref<boolean>,
}

export const differentEmailFromBitrix = ref('');
export const matchedOldEmailFromBitrix = ref('');
/**
 * Набор гетеров для логики смены имейла
 */
export const bitrixEmailChange: EmailChange = {
  isRequestOldEmailOnServiceByBitrixIdDone: ref(false),
  isOldEmailFoundOnServiceByBitrixId: ref(false),
  isFoundDifferentEmailOnServiceByBitrixId: ref(false),
  isReplaceEmailError: ref(false),
  showCompleteEmailChange: ref(false),
  // Базовые гетеры
  hasContactOnService: computed(() => {
    const userStore = useUserStore();
    return (!userStore.getUserInfo.isFetched ? undefined : !!userStore.getUserInfo.id);
  }),
  isNewClient: computed(() => {
    const bitrixStore = useBitrixStore();
    return bitrixStore.client.isNewClient;
  }),
  isCurrentClient: computed(() => {
    const bitrixStore = useBitrixStore();
    return bitrixStore.client.isCurrentClient;
  }),
  isOldClient: computed(() => {
    const bitrixStore = useBitrixStore();
    return bitrixStore.client.isOldClient;
  }),
  hasFieldChangeEmailByClient: computed(() => {
    const bitrixStore = useBitrixStore();
    return !!bitrixStore.client.change_client_email;
  }),
  hasFieldChangeEmailByManager: computed(() => {
    const bitrixStore = useBitrixStore();
    return !!bitrixStore.client.change_b24_email;
  }),
  hasBitrixId: computed(() => {
    const bitrixStore = useBitrixStore();
    return !!bitrixStore.client.bitrix_id;
  }),
  fieldChangeEmailByClient: computed(() => {
    const bitrixStore = useBitrixStore();
    return bitrixStore.client.change_client_email;
  }),
  fieldChangeEmailByManager: computed(() => {
    const bitrixStore = useBitrixStore();
    return bitrixStore.client.change_b24_email;
  }),
  isChangeEmailConfirmed: computed(() => {
    const bitrixStore = useBitrixStore();
    return bitrixStore.client.isChangeEmailConfirmed;
  }),
  isReplaceEmailCompleted: computed(() => {
    const bitrixStore = useBitrixStore();
    return bitrixStore.client.isReplaceEmailCompleted;
  }),
  needReplace: computed(
    () => bitrixEmailChange.isNewClient.value &&
      bitrixEmailChange.hasFieldChangeEmailByClient.value &&
      bitrixEmailChange.isChangeEmailConfirmed.value,
  ),
  needToFindOldEmailOnServiceByBitrixId: computed(
    () => bitrixEmailChange.hasContactOnService.value === false &&
      bitrixEmailChange.isCurrentClient.value &&
      bitrixEmailChange.hasBitrixId.value &&
      !bitrixEmailChange.needReplace.value,
  ),
  // Доп флаг позволяющий задержать общий лоадер страницы, если не всё очевидно
  appLoader: computed(
    () => bitrixEmailChange.needToFindOldEmailOnServiceByBitrixId.value &&
      !bitrixEmailChange.isRequestOldEmailOnServiceByBitrixIdDone.value,
  ),
  // Гетеры для вывода сообщений об ошибках или уведомлений о смене имейла
  bannerNewByChangeEmailNotConfirmed: computed(
    () => bitrixEmailChange.isNewClient.value &&
      bitrixEmailChange.hasFieldChangeEmailByClient.value &&
      !bitrixEmailChange.isChangeEmailConfirmed.value,
  ),
  bannerNewByChangeB24: computed(
    () => bitrixEmailChange.isNewClient.value &&
      bitrixEmailChange.hasFieldChangeEmailByManager.value,
  ),
  modalEmailChangeCompleted: computed(
    () => bitrixEmailChange.isNewClient.value &&
      bitrixEmailChange.isChangeEmailConfirmed.value &&
      bitrixEmailChange.hasFieldChangeEmailByClient.value &&
      bitrixEmailChange.isReplaceEmailCompleted.value,
  ),
  bannerOldClient: computed(
    () => bitrixEmailChange.isOldClient.value,
  ),
  bannerNeedRegistration: computed(
    () => bitrixEmailChange.hasContactOnService.value === false &&
      bitrixEmailChange.isCurrentClient.value &&
      bitrixEmailChange.hasFieldChangeEmailByClient.value &&
      bitrixEmailChange.hasFieldChangeEmailByManager.value &&
      bitrixEmailChange.fieldChangeEmailByClient.value === bitrixEmailChange.fieldChangeEmailByManager.value &&
      bitrixEmailChange.isChangeEmailConfirmed.value,
  ),
  bannerEmailsDontMatch: computed(
    () => bitrixEmailChange.hasContactOnService.value === false &&
      bitrixEmailChange.isCurrentClient.value &&
      bitrixEmailChange.hasFieldChangeEmailByClient.value &&
      bitrixEmailChange.hasFieldChangeEmailByManager.value &&
      bitrixEmailChange.fieldChangeEmailByClient.value !== bitrixEmailChange.fieldChangeEmailByManager.value,
  ),
  modalEmailChangeBlocked: computed(
    () => bitrixEmailChange.hasContactOnService.value === true &&
      bitrixEmailChange.isCurrentClient.value &&
      bitrixEmailChange.hasFieldChangeEmailByManager.value &&
      !bitrixEmailChange.hasFieldChangeEmailByClient.value &&
      !bitrixEmailChange.isChangeEmailConfirmed.value,
  ),
  bannerBadReplace: computed(
    () => bitrixEmailChange.isReplaceEmailError.value,
  ),
  bannerAfterEmailChange: computed(
    () => bitrixEmailChange.hasContactOnService.value === false &&
      bitrixEmailChange.isCurrentClient.value &&
      bitrixEmailChange.hasBitrixId.value,
  ),
  bannerAfterEmailChangeOldEmails: computed(
    () => bitrixEmailChange.hasContactOnService.value === false &&
      bitrixEmailChange.isCurrentClient.value &&
      bitrixEmailChange.hasBitrixId.value &&
      bitrixEmailChange.isOldEmailFoundOnServiceByBitrixId.value,
  ),
  bannerAfterEmailChangeDifferentEmail: computed(
    () => bitrixEmailChange.hasContactOnService.value === false &&
      bitrixEmailChange.isCurrentClient.value &&
      bitrixEmailChange.hasBitrixId.value &&
      bitrixEmailChange.isFoundDifferentEmailOnServiceByBitrixId.value,
  ),
  // Общий компьютед для всех банеров
  hasEmailBanner: computed(
    () => bitrixEmailChange.bannerNewByChangeEmailNotConfirmed.value ||
      bitrixEmailChange.bannerNewByChangeB24.value ||
      bitrixEmailChange.bannerOldClient.value ||
      bitrixEmailChange.bannerNeedRegistration.value ||
      bitrixEmailChange.bannerEmailsDontMatch.value ||
      bitrixEmailChange.bannerBadReplace.value ||
      bitrixEmailChange.bannerAfterEmailChange.value ||
      bitrixEmailChange.bannerAfterEmailChangeOldEmails.value ||
      bitrixEmailChange.bannerAfterEmailChangeDifferentEmail.value,
  ),
};
